import { MagnifyingGlassMinus, MagnifyingGlassPlus } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "../../Buttons/IconButton";
import { ImagePreview, ZoomControls } from "./styles";
import { ImagePreviewPan, clampImagePreviewPan, clampImagePreviewZoom, imagePreviewZoomMax, imagePreviewZoomMin, imagePreviewZoomStep } from "./imageZoom";

export interface ImagePreviewContentProps {
  src: string;
  alt?: string;
  controlsBottomOffset?: number;
  onError?: () => void;
}

export const ImagePreviewContent = ({
  src,
  alt,
  controlsBottomOffset = 0,
  onError,
}: ImagePreviewContentProps ) => {
  const [ imageZoom, setImageZoom ] = useState( imagePreviewZoomMin );
  const [ imagePan, setImagePan ] = useState<ImagePreviewPan>({ x: 0, y: 0 });
  const [ isImageDragging, setIsImageDragging ] = useState( false );
  const imagePreviewRef = useRef<HTMLDivElement>( null );
  const imageRef = useRef<HTMLImageElement>( null );
  const imageDragRef = useRef({
    active: false,
    panX: 0,
    panY: 0,
    pointerId: -1,
    startX: 0,
    startY: 0,
  });

  const clampCurrentImagePan = useCallback(( pan: ImagePreviewPan ) => {
    const preview = imagePreviewRef.current;
    const image = imageRef.current;

    if( !preview || !image ) return pan;

    return clampImagePreviewPan(
      pan,
      { width: preview.clientWidth, height: preview.clientHeight },
      { width: image.offsetWidth, height: image.offsetHeight },
    );
  }, []);

  useEffect(() => {
    setImageZoom( imagePreviewZoomMin );
    setImagePan({ x: 0, y: 0 });
  }, [ src ]);

  useEffect(() => {
    if( imageZoom <= imagePreviewZoomMin ) {
      setIsImageDragging( false );
      setImagePan({ x: 0, y: 0 });
      return;
    }

    setImagePan(( current ) => clampCurrentImagePan( current ));
  }, [ clampCurrentImagePan, imageZoom, src ]);

  const updateImageZoom = ( delta: number ) => {
    setImageZoom(( current ) => clampImagePreviewZoom( current + delta ));
  };

  const stopImageDrag = () => {
    imageDragRef.current.active = false;
    setIsImageDragging( false );
  };

  const handleImagePointerDown = ( event: React.PointerEvent<HTMLDivElement> ) => {
    if( imageZoom <= imagePreviewZoomMin || event.button !== 0 ) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture( event.pointerId );
    imageDragRef.current = {
      active: true,
      panX: imagePan.x,
      panY: imagePan.y,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    setIsImageDragging( true );
  };

  const handleImagePointerMove = ( event: React.PointerEvent<HTMLDivElement> ) => {
    const drag = imageDragRef.current;
    if( !drag.active || drag.pointerId !== event.pointerId ) return;

    event.preventDefault();
    setImagePan( clampCurrentImagePan({
      x: drag.panX + event.clientX - drag.startX,
      y: drag.panY + event.clientY - drag.startY,
    }));
  };

  const handleImagePointerUp = ( event: React.PointerEvent<HTMLDivElement> ) => {
    if( imageDragRef.current.pointerId === event.pointerId && event.currentTarget.hasPointerCapture( event.pointerId )) {
      event.currentTarget.releasePointerCapture( event.pointerId );
    }

    stopImageDrag();
  };

  return (
    <ImagePreview
      ref={ imagePreviewRef }
      $zoom={ imageZoom }
      $panX={ imagePan.x }
      $panY={ imagePan.y }
      $isDragging={ isImageDragging }
      onWheel={( event ) => {
        event.preventDefault();
        updateImageZoom( event.deltaY < 0 ? imagePreviewZoomStep : -imagePreviewZoomStep );
      }}
      onPointerDown={ handleImagePointerDown }
      onPointerMove={ handleImagePointerMove }
      onPointerUp={ handleImagePointerUp }
      onPointerCancel={ handleImagePointerUp }
      onLostPointerCapture={ stopImageDrag }
      onDoubleClick={() => setImageZoom(( current ) => current === imagePreviewZoomMin ? imagePreviewZoomMax : imagePreviewZoomMin )}
    >
      <ZoomControls
        $bottomOffset={ controlsBottomOffset }
        onMouseDown={( event ) => event.stopPropagation()}
        onPointerDown={( event ) => event.stopPropagation()}
      >
        <IconButton
          aria-label="Diminuir zoom"
          title="Diminuir zoom"
          disabled={ imageZoom <= imagePreviewZoomMin }
          onClick={() => updateImageZoom( -imagePreviewZoomStep )}
        >
          <MagnifyingGlassMinus />
        </IconButton>

        <span>{ Math.round( imageZoom * 100 )}%</span>

        <IconButton
          aria-label="Aumentar zoom"
          title="Aumentar zoom"
          disabled={ imageZoom >= imagePreviewZoomMax }
          onClick={() => updateImageZoom( imagePreviewZoomStep )}
        >
          <MagnifyingGlassPlus />
        </IconButton>
      </ZoomControls>

      <img
        ref={ imageRef }
        src={ src }
        alt={ alt }
        onLoad={() => setImagePan(( current ) => clampCurrentImagePan( current ))}
        onError={ onError }
      />
    </ImagePreview>
  );
};
